import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import styles from './IntegrationCard.module.css';

export type IntegrationStatus = 'CONNECTED' | 'DISCONNECTED' | 'ERROR' | 'EXPIRED' | 'PENDING';

interface MetadataField {
  label: string;
  value: string;
}

interface IntegrationCardProps {
  displayName: string;
  description: string;
  icon: LucideIcon;
  status: IntegrationStatus;
  metadata?: MetadataField[];
  errorMessage?: string;
  comingSoon?: boolean;
  /** Replaces the Connect/Reconnect button with a plain note (agency-managed links). */
  managedNote?: string;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onSync?: () => void;
  connectLoading?: boolean;
  disconnectLoading?: boolean;
  syncLoading?: boolean;
}

const STATUS_BADGE: Record<
  IntegrationStatus,
  { variant: 'success' | 'error'; label: string } | null
> = {
  CONNECTED: { variant: 'success', label: 'Connected' },
  DISCONNECTED: null,
  ERROR: { variant: 'error', label: 'Error' },
  EXPIRED: { variant: 'error', label: 'Expired' },
  PENDING: null,
};

export function IntegrationCard({
  displayName,
  description,
  icon: Icon,
  status,
  metadata,
  errorMessage,
  comingSoon,
  managedNote,
  onConnect,
  onDisconnect,
  onSync,
  connectLoading,
  disconnectLoading,
  syncLoading,
}: IntegrationCardProps) {
  const badge = STATUS_BADGE[status];
  const isErrored = status === 'ERROR' || status === 'EXPIRED';
  const isConnected = status === 'CONNECTED';

  return (
    <div className={`${styles.card} ${comingSoon ? styles.cardMuted : ''}`}>
      <div className={styles.left}>
        <div className={styles.icon}>
          <Icon size={24} strokeWidth={1.75} aria-hidden />
        </div>
        <div className={styles.info}>
          <div className={styles.nameRow}>
            <h4 className={styles.name}>{displayName}</h4>
            {comingSoon && <Badge variant="neutral">Coming Soon</Badge>}
            {!comingSoon && badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
          </div>
          <p className={styles.description}>{description}</p>
          {!comingSoon &&
            metadata?.map((m) => (
              <p key={m.label} className={styles.syncInfo}>
                {m.label}: {m.value}
              </p>
            ))}
          {!comingSoon && errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
        </div>
      </div>
      {!comingSoon && (
        <div className={styles.right}>
          {isConnected && onSync && (
            <Button size="sm" variant="secondary" onClick={onSync} isLoading={syncLoading}>
              Sync Now
            </Button>
          )}
          {isConnected && onDisconnect && (
            <Button
              size="sm"
              variant="tertiary"
              onClick={onDisconnect}
              isLoading={disconnectLoading}
            >
              Disconnect
            </Button>
          )}
          {!isConnected && managedNote && <span className={styles.managedNote}>{managedNote}</span>}
          {!isConnected && !managedNote && onConnect && (
            <Button size="sm" onClick={onConnect} isLoading={connectLoading}>
              {isErrored ? 'Reconnect' : 'Connect'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
